import { keepPreviousData, useQuery, useMutation } from '@tanstack/react-query';
import type { AssignRoleRequest, CreateUserRequest, DeactivateUserRequest, UpdateAgronomistRegionsRequest, UpdateUserRequest } from '../services/userApi';
import { usersApi } from '../services/userApi';
import { queryClient } from '@/shared/services/queryClient';

export const useUsers = (pageNumber: number, pageSize: number, search?: string) => {
  return useQuery({
    queryKey: ['users', pageNumber, pageSize, search],
    queryFn: () => usersApi.getAllUsers(pageNumber, pageSize, search),
    placeholderData: keepPreviousData,
  });
};

export const useUserById = (id: string) => {
  return useQuery({
    queryKey: ['users', id],
    queryFn: () => usersApi.getUserById(id),
  });
};

export const useCreateUser = () => {
  return useMutation({
    mutationFn: (payload: CreateUserRequest) => usersApi.createUser(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

export const useUpdateUser = (id: string) => {
  return useMutation({
    mutationFn: (payload: UpdateUserRequest) => usersApi.updateUser(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['users', id] });
    },
  });
};

export const useDeactivateUser = () => {
  return useMutation({
    mutationFn: (data: { id: string; payload: DeactivateUserRequest }) =>
      usersApi.deactivateUser(data.id, data.payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

export const useReactivateUser = () => {
  return useMutation({
    mutationFn: (id: string) => usersApi.reactivateUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

export const useAssignRole = () => {
  return useMutation({
    mutationFn: (data: { id: string; payload: AssignRoleRequest }) =>
      usersApi.assignRole(data.id, data.payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

export const useRemoveRole = () => {
  return useMutation({
    mutationFn: (data: { id: string; payload: AssignRoleRequest }) =>
      usersApi.removeRole(data.id, data.payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

export const useUpdateAgronomistRegions = () => {
  return useMutation({
    mutationFn: (data: { id: string; payload: UpdateAgronomistRegionsRequest }) =>
      usersApi.updateAgronomistRegions(data.id, data.payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

export const useUsersByPermission = (permission: string) => {
  return useQuery({
    queryKey: ['users-by-permission', permission],
    queryFn: () => usersApi.getUsersByPermission(permission),
    staleTime: 5 * 60 * 1000,
  });
};
